import {Badge} from '@mantine/core';
import React from 'react';

import {archiveAnalysis, unarchiveAnalysis} from 'analysis-tool/AnalysisActions';
import {
  ADVANCED_ANALYSIS_ACCESS_MESSAGE,
  isAnalysisTemplatesEnabled,
} from 'analysis-tool/AnalysisToolFeatureGates';
import {
  getArchivedBannerMessage,
  getArchivedTooltipMessage,
  getStepLimitWarningText,
} from 'analysis-tool/AnalysisToolMessages';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import CloneAnalysisModal from 'analysis-tool/CloneAnalysisModal';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import isAnalysisSucceeded from 'analysis-tool/isAnalysisSucceeded';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import BaseAnalysisUIHeader, {READ_ONLY_TOOLTIP_TEXT} from 'analysis-tool/ui/header/BaseAnalysisUIHeader';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import AnalysisMetadataModal from 'analysis-tool/ui/metadata-modal/AnalysisMetadataModal';
import ArchiveModal, {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BreadcrumbItem} from 'ui-library/Breadcrumb';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import removeNulls from 'util/removeNulls';

function getCopyAnalysisDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (isAnalysisRunning(analysis)) {
    return 'Cannot duplicate analysis while it is running.';
  }

  return undefined;
}

function getCreateTemplateIsDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (!isAnalysisTemplatesEnabled()) {
    return ADVANCED_ANALYSIS_ACCESS_MESSAGE;
  } else if (!isAnalysisSucceeded(analysis)) {
    return 'Cannot create template from an analysis that has not succeeded.';
  } else if (analysis.execution.steps.length === 0) {
    return 'Cannot create template from an empty analysis.';
  } else if (
    analysis.execution.steps.some((step) => step.definition.stepType === PipelineStepType.FLAG_ROWS)
  ) {
    return 'Analysis with flagged rows cannot be used to create a template.';
  }

  return undefined;
}

function getApplyTemplateIsDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (!isAnalysisTemplatesEnabled()) {
    return ADVANCED_ANALYSIS_ACCESS_MESSAGE;
  } else if (!analysis.permissions.writable) {
    return 'You do have access to modify this analysis.';
  }

  return undefined;
}

function getOverflowMenuOptions(
  analysis: AnalysisGraphQL,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void
): Array<OverflowMenuOption> {
  return removeNulls([
    {
      icon: 'template',
      isDisabled: getCreateTemplateIsDisabledMessage(analysis) != null,
      tooltipTitle: getCreateTemplateIsDisabledMessage(analysis),
      contents: <span>Save as analysis template</span>,
      onSelect: () => {
        onOpenStepInitializationView({
          type: AnalysisMainViewItemType.TEMPLATE_CONFIGURATION,
        });
      },
      dataTestAttrs: {
        component: 'AnalysisUIHeader',
        element: 'saveAsAnalysisTemplateOption',
      },
    },
    {
      icon: 'template',
      isDisabled: getApplyTemplateIsDisabledMessage(analysis) != null,
      tooltipTitle: getApplyTemplateIsDisabledMessage(analysis),
      contents: <span>Apply analysis template</span>,
      onSelect: () => {
        onOpenStepInitializationView({
          type: AnalysisMainViewItemType.TEMPLATE_APPLY,
        });
      },
      dataTestAttrs: {
        component: 'AnalysisUIHeader',
        element: 'applyAnalysisTemplateOption',
      },
    },
    {
      icon: 'copy',
      isDisabled: getCopyAnalysisDisabledMessage(analysis) != null,
      tooltipTitle: getCopyAnalysisDisabledMessage(analysis),
      contents: <span>Duplicate</span>,
      onSelect: () => {
        runModal(CloneAnalysisModal, {analysisToClone: analysis});
      },
    },
    {
      icon: 'info-circle',
      contents: <span>View metadata</span>,
      onSelect: () => {
        runModal(AnalysisMetadataModal, {analysis});
      },
      dataTestAttrs: {component: 'AnalysisUIHeader', element: 'viewMetadataOption'},
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([analysis.id]);
      },
    },
    analysis.permissions.archivable && !isArchived(analysis)
      ? {
          icon: 'archive',
          isDisabled: isAnalysisRunning(analysis),
          tooltipTitle: isAnalysisRunning(analysis)
            ? 'Cannot archive analysis while it is running'
            : undefined,
          contents: <span>Archive analysis</span>,
          onSelect: () => {
            runModal(ArchiveModal, {
              items: [analysis],
              onArchive: (
                _itemIds: Array<string>,
                purpose: string,
                options: ArchiveOptionsForModal
              ): void => {
                archiveAnalysis(analysis.id, purpose, options.resultPurpose ?? null);
              },
            });
          },
        }
      : null,
  ]);
}

function getBannerStackMessages({
  analysis,
  actions,
}: {
  analysis: AnalysisGraphQL;
  actions: {
    unarchive?: () => Promise<void>;
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  };
}): Array<InlineBannerProps> {
  const messages: Array<InlineBannerProps> = [];
  if (isArchived(analysis)) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'box-archive',
      action:
        actions.unarchive != null
          ? {
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Unarchive',
              actionOnClick: actions.unarchive,
            }
          : undefined,
      text: getArchivedBannerMessage(analysis, analysis.archiveRecord.purpose),
      supportingInfo: actions.unarchive != null ? `${getArchivedTooltipMessage(analysis)}.` : undefined,
      dataTestAttrs: {component: 'ArchivedBanner', element: 'root'},
    });
  } else if (analysis.steps.length >= BenchlingConfig.PIPELINES_MAX_STEP_COUNT * 0.9) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'exclamation-triangle',
      text: getStepLimitWarningText(analysis, analysis.steps.length),
      supportingInfo: isAnalysisTemplatesEnabled()
        ? 'A template created from this analysis might not be able to be applied.'
        : undefined,
    });
  }
  return messages;
}

function getAnalysisBreadcrumbInfo(analysis: AnalysisGraphQL): {
  items: Array<BreadcrumbItem>;
  icon: 'folder-open';
} {
  const items: Array<BreadcrumbItem> = [
    {
      label: analysis.project.name,
      href: analysis.project.url,
    },
  ];

  if (analysis.folder.url !== analysis.project.url) {
    items.push({
      label: analysis.folder.name,
      href: analysis.folder.url,
    });
  }

  return {items, icon: 'folder-open'};
}

export interface AnalysisUIHeaderProps {
  analysis: AnalysisGraphQL;
  showOpenSidebarButton: boolean;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
  onOpenRightSidebarItem?: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenSidebar: () => void;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
}

export default function AnalysisUIHeader({
  analysis,
  showOpenSidebarButton,
  onChangeSelectedItem,
  onOpenRightSidebarItem,
  onOpenSidebar,
  onOpenStepInitializationView,
}: AnalysisUIHeaderProps): JSX.Element {
  const overflowMenuOptions = getOverflowMenuOptions(analysis, onOpenStepInitializationView);
  const bannerStackMessages = getBannerStackMessages({
    analysis,
    actions: {
      unarchive: analysis.permissions.unarchivable
        ? async () => {
            await unarchiveAnalysis(analysis.id);
          }
        : undefined,
      onOpenStepInitializationView,
    },
  });
  const breadcrumbInfo = getAnalysisBreadcrumbInfo(analysis);

  const isReadOnly = !(
    analysis.permissions.writable ||
    analysis.permissions.unarchivable ||
    analysis.permissions.archivable
  );

  return (
    <BaseAnalysisUIHeader
      pipeline={analysis}
      showOpenSidebarButton={showOpenSidebarButton}
      onChangeSelectedItem={onChangeSelectedItem}
      onOpenRightSidebarItem={onOpenRightSidebarItem}
      onOpenSidebar={onOpenSidebar}
      onOpenStepInitializationView={onOpenStepInitializationView}
      overflowMenuOptions={overflowMenuOptions}
      bannerStackMessages={bannerStackMessages}
      breadcrumbInfo={breadcrumbInfo}
      canCreateOutputs={!isReadOnly}
    >
      {isReadOnly && (
        <Tooltip text={READ_ONLY_TOOLTIP_TEXT} placement={FloatingPlacement.BOTTOM}>
          <Badge color="yellow" size="xl">
            Read-only
          </Badge>
        </Tooltip>
      )}
    </BaseAnalysisUIHeader>
  );
}
